import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { isApiError } from "@bw/shared/api";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { useMyProfile } from "../common/query/useMyProfile";

// The account_status values that may reach the app shell. The gate is
// fail-CLOSED (#1586): everything else is held out, so a status the backend
// introduces later (suspended / banned) can never silently sail into the shell
// on a fail-OPEN allowlist-the-one-bad-status check.
//
// `pending_parental_consent` is a child under 13 (COPPA) with no active guardian, or
// whose guardian paused the account. Since #2691 they browse the app in
// look-only mode: the shell shows a persistent "ask a parent or guardian"
// banner and the server refuses every controlled action (GUARDIAN_REQUIRED).
const SHELL_STATUSES: ReadonlySet<string> = new Set([
  "active",
  "pending_parental_consent",
]);

interface GateSurfaceProps {
  children: ReactNode;
  role?: "alert";
  testId?: string;
}

function GateSurface({ children, role, testId }: GateSurfaceProps) {
  return (
    <Box
      component="main"
      role={role}
      data-testid={testId}
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Paper
        variant="outlined"
        sx={{ width: "min(100%, 32rem)", p: { xs: 3, sm: 4 } }}
      >
        <Stack spacing={2}>{children}</Stack>
      </Paper>
    </Box>
  );
}

/**
 * RequireProfile gate (player Slice 2).
 *
 * getMyProfile is the single source of truth for "does this authed user have a
 * profile?":
 *
 *   - ANONYMOUS          → <Outlet/> — PUBLIC pass-through. The home (`/`) is
 *                          public-browse, so an anonymous visitor falls straight
 *                          through WITHOUT firing the authed profile read (no 401).
 *   - loading            → a token-styled spinner (role="status"); no premature
 *                          redirect, no Outlet.
 *   - 200 (profile)      → <Outlet/> — the app shell + routes mount.
 *   - 404 (no profile)   → <Navigate to="/onboarding" replace/>.
 *   - other error        → an inline retry state (NOT a redirect, NOT the app).
 *
 * It is mounted in TWO places: (1) the PUBLIC home branch (outside AuthGuard) —
 * where the anon pass-through above lets the public home render while still
 * routing a freshly-registered, profile-less authed user to onboarding; and (2)
 * the guarded subtree (inside AuthGuard, where `user` is always present, so the
 * anon branch is a no-op there).
 *
 * /onboarding is a SIBLING of the guarded subtree but still under AuthGuard, so an
 * authed no-profile user reaches it without a redirect loop. Re-login resume is
 * stateless: a returning, fully-onboarded user gets a fresh 200 → app; one who
 * bailed gets 404 → /onboarding. No client flag, no localStorage.
 *
 * Lives in app/ (router-level chrome), not a module — it gates the whole shell.
 */
export function RequireProfile() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  // Gate the authed profile read on auth state: an anonymous public-browse
  // visitor must not fire GET /users/profile (it would 401).
  const { data, isLoading, isError, error, refetch } = useMyProfile(
    Boolean(user),
  );

  // Public-browse pass-through: no session → render the (public) child as-is.
  if (!user) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <Stack
        component="main"
        role="status"
        aria-live="polite"
        spacing={2}
        sx={{
          minHeight: "100dvh",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress aria-hidden="true" />
        <Typography>Loading your profile…</Typography>
      </Stack>
    );
  }

  // No profile yet → onboarding. The 404 is the genuine ApiError from httpClient.
  // #1626: forward the destination the user was trying to reach as ?returnUrl so
  // onboarding can send them back there (e.g. a parent bounced from
  // /family/approvals). Generic + URL-driven — never hardcoded to one route. A
  // bounce from the home ("/") stays a bare /onboarding (no noisy param).
  if (isError && isApiError(error) && error.status === 404) {
    const dest = location.pathname + location.search;
    const to =
      dest && dest !== "/"
        ? `/onboarding?returnUrl=${encodeURIComponent(dest)}`
        : "/onboarding";
    return <Navigate to={to} replace />;
  }

  // Any other failure (network, 5xx) → an honest inline retry, never a silent
  // bounce to onboarding and never the app shell.
  if (isError || !data || data.user_id !== user.uid) {
    return (
      <GateSurface role="alert">
        <Alert severity="error" variant="outlined">
          We couldn&apos;t load your profile
        </Alert>
        <Typography color="text.secondary">
          Something went wrong reaching the server. Check your connection and
          try again.
        </Typography>
        <Button variant="contained" onClick={() => refetch()}>
          Try again
        </Button>
      </GateSurface>
    );
  }

  // Fail-CLOSED account-status gate (#1586): the profile EXISTS, but ONLY an
  // active or look-only (waiting for a guardian) account may mount the app
  // shell. Any other status is held out with the RIGHT message — so a status the
  // backend adds later (suspended / banned) can never silently reach the shell,
  // and a suspended adult is NOT shown the "wait for a parent" copy.
  if (!SHELL_STATUSES.has(data.account_status ?? "")) {
    // Any other status (e.g. suspended / banned). A generic terminal screen —
    // never the app shell, never the consent copy.
    return (
      <GateSurface role="alert" testId="account-inactive">
        <Typography component="h1" variant="h2">
          Your account isn&apos;t active
        </Typography>
        <Typography color="text.secondary">
          Your account isn&apos;t active right now, so you can&apos;t access the
          app. If you think this is a mistake, please contact support.
        </Typography>
        <Button variant="outlined" onClick={() => signOut()}>
          Sign out
        </Button>
      </GateSurface>
    );
  }

  return <Outlet />;
}
